/**
 * Conference Actions Component
 * Provides role-based action menus for video conferences with 3-dot dropdown
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';

interface ConferenceActionsProps {
  conferenceId: string;
  conferenceTitle: string;
  status: string;
  isHost: boolean;
  isAttendee: boolean;
  hasRecording: boolean;
  onView: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onViewRecording?: () => void;
  disabled?: boolean;
}

export function ConferenceActions({
  conferenceTitle,
  status,
  isHost,
  hasRecording,
  onView,
  onEdit,
  onCancel,
  onViewRecording,
  disabled,
}: ConferenceActionsProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = 300; // Approximate max menu height
      const menuWidth = 192; // w-48 = 12rem = 192px
      
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Calculate position
      let top: number;
      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        // Open upward
        top = rect.top - menuHeight + window.scrollY;
      } else {
        // Open downward
        top = rect.bottom + window.scrollY + 4;
      }
      
      // Ensure menu doesn't go off the right edge
      let left = rect.right - menuWidth + window.scrollX;
      if (left < 8) left = 8;
      
      setMenuStyle({
        position: 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        width: `${menuWidth}px`,
      });
    }
  }, [showMenu]);

  // Close menu on scroll
  useEffect(() => {
    if (!showMenu) return;
    
    const handleScroll = () => setShowMenu(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showMenu]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      setShowCancelConfirm(false);
      setShowMenu(false);
    }
  };

  const menuContent = showMenu && (
    <>
      <div
        className="fixed inset-0 z-[100]"
        onClick={() => setShowMenu(false)}
      />
      <div 
        style={menuStyle}
        className="bg-white rounded-lg shadow-lg border border-gray-200 z-[101]"
      >
        <div className="py-1">
          {/* View Details - always visible */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView();
              setShowMenu(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            View Details
          </button>

          {/* Edit - host only, scheduled only */}
          {status === 'SCHEDULED' && isHost && onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Conference
            </button>
          )}

          {/* View Recording - completed with recording */}
          {status === 'COMPLETED' && hasRecording && onViewRecording && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewRecording();
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View Recording
            </button>
          )}

          {/* Cancel - host only, scheduled only */}
          {status === 'SCHEDULED' && isHost && onCancel && (
            <>
              {!showCancelConfirm ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCancelConfirm(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel Conference
                </button>
              ) : (
                <div className="px-4 py-3 bg-red-50">
                  <p className="text-xs text-red-800 mb-2">Cancel "{conferenceTitle}"?</p>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancel();
                      }}
                      className="flex-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCancelConfirm(false);
                      }}
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
          setShowCancelConfirm(false);
        }}
        className="text-gray-600 hover:text-aalaw-red"
        disabled={disabled}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </Button>

      {createPortal(menuContent, document.body)}
    </div>
  );
}
